import React, { useState, useEffect } from 'react';
import { authClient } from '../lib/auth';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialIsLogin?: boolean;
  initialTierId?: string;
  onProceedToCheckout?: (tierId: string) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess, initialIsLogin = true, initialTierId, onProceedToCheckout }) => {
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    const handle2FA = () => setRequires2FA(true);
    window.addEventListener('require-2fa', handle2FA);
    return () => window.removeEventListener('require-2fa', handle2FA);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (requires2FA) {
        const { error } = await authClient.twoFactor.verifyTotp({
          code: totpCode,
        });
        if (error) {
          setError(error.message || 'Error');
        } else {
          onSuccess();
          onClose();
        }
      } else if (isLogin) {
        const { error } = await authClient.signIn.email({
          email,
          password
        });
        if (error) {
          setError(error.message || 'Error');
        } else if (!requires2FA) {
          setTimeout(() => {
            setRequires2FA(prev => {
              if (!prev) {
                if (initialTierId && onProceedToCheckout) {
                  onProceedToCheckout(initialTierId);
                } else {
                  onSuccess();
                }
                onClose();
              }
              return prev;
            });
          }, 100);
        }
      } else {
        // Sign Up Flow
        const { error: signUpError } = await authClient.signUp.email({
          email,
          password,
          name,
        });

        if (signUpError) {
          setError(signUpError.message || 'Error creating account');
        } else {
          // Immediately after sign up, they are logged in by BetterAuth.
          // Wait a tick for session to establish
          setTimeout(async () => {
            // Also automatically create an organization for them
            const { data: orgData, error: orgError } = await authClient.organization.create({
              name: `${name}'s Family Vault`,
              slug: name.toLowerCase().replace(/[^a-z0-9]/g, '-') + '-' + Math.random().toString(36).substring(2, 6)
            });

            if (orgError) {
              console.error("Failed to create org", orgError);
            } else if (orgData) {
              await authClient.organization.setActive({ organizationId: orgData.id });
            }

            if (initialTierId && onProceedToCheckout) {
              onProceedToCheckout(initialTierId);
            } else {
              onSuccess();
            }
            onClose();
          }, 500);
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      if (isLogin) setLoading(false);
      // For sign up, we keep loading true while we create org and redirect
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="glass-panel" style={{ padding: '2.5rem', width: '100%', maxWidth: '400px', background: 'var(--bg-primary)' }}>
        <h2 style={{ fontSize: '1.5rem', marginTop: 0, marginBottom: '1.5rem', textAlign: 'center' }}>
          {requires2FA ? 'Two-Factor Authentication' : isLogin ? 'Welcome Back' : 'Create Account'}
        </h2>

        {error && (
          <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {requires2FA ? (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Authenticator Code</label>
              <input
                type="text"
                required
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.5rem' }}
                placeholder="123456"
              />
            </div>
          ) : (
            <>
              {!isLogin && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Name</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    placeholder="Jane Doe"
                  />
                </div>
              )}
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  placeholder="you@example.com"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  placeholder="••••••••"
                />
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginBottom: '1rem' }} disabled={loading}>
            {loading ? 'Please wait...' : (requires2FA ? 'Verify Code' : isLogin ? 'Sign In' : 'Sign Up')}
          </button>
        </form>

        {!requires2FA && (
          <div style={{ textAlign: 'center', fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div>
              {isLogin ? (
                <span style={{ color: 'var(--text-secondary)' }}>
                  Don't have an account? <a href="#" onClick={(e) => { e.preventDefault(); setIsLogin(false); }} style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>Sign Up</a>
                </span>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>
                  Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); setIsLogin(true); }} style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>Sign In</a>
                </span>
              )}
            </div>
          </div>
        )}
        
        <button 
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.5rem', marginTop: '1rem', width: '100%' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
